import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  UsersIcon,
  ServerIcon,
  ChatBubbleLeftRightIcon,
  PlusIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { api, getErrorMessage, isPermissionError } from '../services/api';
import toast from '../utils/toast';
import ConnectionsTable from '../components/ConnectionsTable';
import { PermissionGuard } from '../components/PermissionGuard';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalClients: 0,
    runningContainers: 0,
    totalMessages: 0,
    activeChats: 0
  });
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [clientsResponse, dashboardStatsResponse, agentsResponse, subscriptionResponse] = await Promise.all([
        api.getClients(),
        api.getDashboardStats().catch(() => ({ data: { stats: {} } })),
        api.getAgents().catch(() => ({ data: { agents: [] } })),
        api.getSubscriptionStatus().catch(() => ({ data: null }))
      ]);

      const clientsData = clientsResponse.data.clients || {};
      const clientList = Object.values(clientsData);

      // Sort clients by created_at date, latest first
      const sortedClients = clientList.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA; // Latest first
      });

      setClients(sortedClients);

      // Set agents
      const activeAgents = agentsResponse.data.agents?.filter(agent => agent.status === 'active') || [];
      setAgents(activeAgents);

      // Set subscription status
      if (subscriptionResponse.data && subscriptionResponse.data.widget) {
        setSubscriptionStatus(subscriptionResponse.data.widget);
      }

      // Use real database stats from the API
      const dbStats = dashboardStatsResponse.data.stats || {};
      setStats({
        totalClients: dbStats.totalClients || 0,
        runningContainers: dbStats.runningContainers || 0,
        totalMessages: dbStats.totalMessages || 0,
        activeChats: dbStats.activeChats || 0
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);

      if (isPermissionError(error)) {
        toast.error(getErrorMessage(error));
        // You could also show a more detailed permission error component
      } else {
        toast.error(getErrorMessage(error));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleClientUpdate = (clientId, updateData) => {
    // Update local state immediately for better UX
    setClients(prevClients =>
      prevClients.map(client =>
        client.client_id === clientId
          ? { ...client, ...updateData }
          : client
      )
    );
  };

  const handleClientDelete = (clientId) => {
    // Remove client from local state and refresh stats
    setClients(prevClients =>
      prevClients.filter(client => client.client_id !== clientId)
    );
    // Refresh dashboard data to update stats
    fetchData();
  };

  const statCards = [
    {
      name: 'Total Agents',
      value: stats.totalClients,
      icon: UsersIcon,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600'
    },
    {
      name: 'Running Processes',
      value: stats.runningContainers,
      icon: ServerIcon,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600'
    },
    {
      name: 'Active Chats',
      value: stats.activeChats,
      icon: ChatBubbleLeftRightIcon,
      color: 'bg-whatsapp-500',
      bgColor: 'bg-whatsapp-50',
      textColor: 'text-whatsapp-600'
    },
    {
      name: 'Total Messages',
      value: stats.totalMessages,
      icon: ChatBubbleLeftRightIcon,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-whatsapp-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Manage your WhatsApp AI agents</p>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <button
            onClick={fetchData}
            className="btn-secondary btn-sm"
            disabled={loading}
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <PermissionGuard permission="agents.create">
            <Link to="/dashboard/agents/create" className="btn-primary">
              <PlusIcon className="h-5 w-5 mr-2" />
              Create Agent
            </Link>
          </PermissionGuard>
        </div>
      </div>

      {/* Trial/Subscription Banner */}
      {subscriptionStatus && (
        <>
          {/* Active Trial Banner */}
          {subscriptionStatus.trial?.is_trial && subscriptionStatus.trial?.days_remaining > 0 && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <SparklesIcon className="h-6 w-6 text-amber-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Free Trial Active
                    </h3>
                    <p className="text-gray-600">
                      {subscriptionStatus.trial.days_remaining} days remaining in your free trial
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Link
                    to="/dashboard/billing/usage"
                    className="btn-secondary"
                  >
                    View Usage
                  </Link>
                  <Link
                    to="/dashboard/billing/subscription"
                    className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2 rounded-lg font-medium hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center"
                  >
                    <SparklesIcon className="h-4 w-4 mr-2" />
                    Upgrade Now
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Expired Trial Banner - Only show if trial is NOT active or has no remaining days */}
          {(!subscriptionStatus.trial?.is_trial || subscriptionStatus.trial?.days_remaining <= 0) &&
           (subscriptionStatus.access?.requires_payment === true ||
            subscriptionStatus.subscription_status === 'expired' ||
            subscriptionStatus.subscription_status === 'trial') && (
            <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Plan Expired - Upgrade Required
                    </h3>
                    <p className="text-gray-600">
                      Your current plan has ended. Upgrade now to continue using all features.
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Link
                    to="/dashboard/billing/usage"
                    className="btn-secondary"
                  >
                    View Usage
                  </Link>
                  <Link
                    to="/dashboard/billing/subscription"
                    className="bg-gradient-to-r from-red-600 to-pink-600 text-white px-6 py-2 rounded-lg font-medium hover:from-red-700 hover:to-pink-700 transition-all duration-200 flex items-center animate-pulse"
                  >
                    <SparklesIcon className="h-4 w-4 mr-2" />
                    Upgrade Now
                  </Link>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Stats Grid */}
      <PermissionGuard permission={["clients.view", "dashboard.view"]} fallback={
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">You need dashboard viewing permissions to see statistics.</p>
        </div>
      }>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.name} className="card">
                <div className="flex items-center">
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`h-6 w-6 ${stat.textColor}`} />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">{stat.name}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </PermissionGuard>

      {/* Recent Assistants */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Recent Agents</h2>
          <Link to="/dashboard/connections" className="text-whatsapp-600 hover:text-whatsapp-700 text-sm font-medium">
            View all →
          </Link>
        </div>

        {clients.length === 0 ? (
          <div className="text-center py-16">
            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-gradient-to-r from-whatsapp-500 to-green-600 mb-6">
              <UsersIcon className="h-10 w-10 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">No agents yet</h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">Get started by creating your first WhatsApp agent and begin managing your conversations.</p>
            <PermissionGuard permission="agents.create">
              <Link
                to="/dashboard/agents/create"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-whatsapp-500 to-green-600 text-white font-semibold rounded-xl hover:from-whatsapp-600 hover:to-green-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <PlusIcon className="h-6 w-6 mr-3" />
                Create Your First Agent
              </Link>
            </PermissionGuard>
          </div>
        ) : (
          <ConnectionsTable
            clients={clients}
            agents={agents}
            onClientUpdate={handleClientUpdate}
            onClientDelete={handleClientDelete}
            showDeleteAction={false}
            maxRows={5}
            showMobilePort={true}
          />
        )}
      </div>
    </div>
  );
};

export default Dashboard;
